// Bruits de pas : un son très léger, synthétisé, joué à chaque pas du joueur sur une case dont un élément (terrain ou
// objet posé) a un sol choisi dans l'éditeur d'éléments. Partagé par le jeu (qui le joue) et l'administration.
import type { Position } from './protocol.ts';
import { inDrawingOrder, terrainsAt, type MapElement, type MapLayout, type MapObject } from './map-elements.ts';

export const FOOTSTEPS = {
  grass: 'Herbe',
  stone: 'Pierre, pavés',
  wood: 'Bois, plancher',
  sand: 'Sable, terre',
  snow: 'Neige',
  gravel: 'Gravier',
  water: 'Flaque, eau peu profonde',
  carpet: 'Tapis, moquette',
} as const;
export type Footstep = keyof typeof FOOTSTEPS;
export const isFootstep = (value: unknown): value is Footstep => typeof value === 'string' && Object.hasOwn(FOOTSTEPS, value);

/** Sol des objets posés, case par case (clé « x,y »), calculé une fois par liste d'objets (elle est remplacée à chaque changement). */
const objectFloors = new WeakMap<readonly MapObject[], { elements: ReadonlyMap<number, MapElement>; floors: Map<string, Footstep> }>();

function objectFloorsOf(objects: readonly MapObject[], elements: ReadonlyMap<number, MapElement>): Map<string, Footstep> {
  const known = objectFloors.get(objects);
  if (known?.elements === elements) return known.floors;
  const floors = new Map<string, Footstep>();
  // Dans l'ordre de dessin : l'objet le plus haut qui a un sol, dessiné le dernier, l'emporte.
  for (const object of inDrawingOrder(objects)) {
    const element = elements.get(object.elementId);
    const step = element && (element.footstep ?? (element.underlay === null ? null : elements.get(element.underlay)?.footstep ?? null));
    if (!step) continue;
    for (let dy = 0; dy < element.height; dy++) for (let dx = 0; dx < element.width; dx++) floors.set(`${object.x + dx},${object.y + dy}`, step);
  }
  objectFloors.set(objects, { elements, floors });
  return floors;
}

/**
 * Sol de la case : celui de l'objet posé le plus haut qui la couvre et en a un (pont, tapis…, ou le terrain dessiné
 * sous lui), sinon celui du terrain le plus haut qui en a un ; null : pas de bruit.
 */
export function footstepAt(map: Pick<MapLayout, 'width' | 'tiles' | 'upperTiles'> & { objects: readonly MapObject[] }, elements: ReadonlyMap<number, MapElement>, { x, y }: Position): Footstep | null {
  const floor = objectFloorsOf(map.objects, elements).get(`${x},${y}`);
  if (floor) return floor;
  for (const id of terrainsAt(map, y * map.width + x).reverse()) {
    const step = elements.get(id)?.footstep;
    if (step) return step;
  }
  return null;
}
